/* Desktop row for a movie feed item. Shows poster, title, director, year, runtime, rating, genres. */

import { memo, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Plus, X, Check, Archive, MoreVertical, Star, Clock } from "lucide-react";
import { ThumbnailPlaceholder } from "@/components/feed/ThumbnailPlaceholder";
import { useUserSettings } from "@/hooks/useUserSettings";
import type { FeedItemData } from "@/components/feed/FeedItemRow";

interface MovieItemRowProps {
  item: FeedItemData;
  onOpen?: () => void;
  onDelete?: () => void;
  onComplete?: () => void;
  onQueue?: () => void;
  onBacklog?: () => void;
}

function clearOtherActiveRows(current: HTMLElement) {
  document.querySelectorAll('[data-slot="feed-row"][data-active]').forEach(el => {
    if (el !== current) el.removeAttribute('data-active');
  });
}

export const MovieItemRow = memo(function MovieItemRow({ item, onOpen, onDelete, onComplete, onQueue, onBacklog }: MovieItemRowProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isPendingAction, setIsPendingAction] = useState(false);
  const { animationsEnabled } = useUserSettings();

  const transition = animationsEnabled ? "transition-all duration-200" : "";

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (rowRef.current?.hasAttribute('data-active') && !rowRef.current.contains(e.target as Node)) {
        rowRef.current.removeAttribute('data-active');
        rowRef.current.blur();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const posterUrl = item.moviePoster ?? item.thumbnail;

  function handleFocus() {
    if (rowRef.current) {
      clearOtherActiveRows(rowRef.current);
      rowRef.current.setAttribute('data-active', '');
    }
  }

  function runAction(action?: () => void) {
    if (!action) return;
    setIsPendingAction(true);
    action();
  }

  function handleKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case " ":
        e.preventDefault();
        onOpen?.();
        break;
      case "x":
      case "ArrowLeft":
        e.preventDefault();
        runAction(onDelete);
        break;
      case "c":
        e.preventDefault();
        runAction(onComplete);
        break;
      case "q":
      case "ArrowRight":
        e.preventDefault();
        runAction(onQueue);
        break;
      case "b":
        e.preventDefault();
        runAction(onBacklog);
        break;
      case "Escape":
        e.preventDefault();
        rowRef.current?.removeAttribute('data-active');
        rowRef.current?.blur();
        break;
    }
  }

  return (
    <div
      ref={rowRef}
      data-slot="feed-row"
      tabIndex={0}
      className={`group w-full md:max-w-[896px] flex items-start gap-2.5 px-4 md:px-6 py-2 hover:bg-muted/50 data-[active]:bg-muted/50 outline-none rounded-sm ${transition} ${isPendingAction ? "hidden" : ""}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
    >
      {/* Movie poster (2:3) */}
      <div
        className={`relative shrink-0 h-[68px] cursor-pointer ${transition}`}
        style={{ aspectRatio: "2 / 3" }}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => onOpen?.()}
      >
        {posterUrl ? (
          <img
            src={posterUrl}
            alt={item.title}
            className="w-full h-full rounded-sm object-cover bg-muted"
          />
        ) : (
          <ThumbnailPlaceholder contentType="movie" />
        )}
      </div>

      <div className="flex items-start justify-between flex-1 min-w-0">
        <div className="flex flex-col items-start gap-1.5 min-w-0 pt-0.5">
          <p
            className={`text-sm font-medium leading-tight truncate max-w-full cursor-pointer hover:underline ${transition}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onOpen?.()}
          >
            {item.sourceStarred && <Star className="inline size-3 fill-current text-primary mr-1 align-baseline" />}
            {item.title}
          </p>

          <div className="flex items-center gap-2 min-w-0 max-w-full flex-wrap">
            {item.movieDirector && (
              <span className={`text-xs text-muted-foreground shrink-0 ${transition}`}>
                {item.movieDirector}
              </span>
            )}
            {item.movieYear && (
              <>
                <span className={`text-xs text-muted-foreground shrink-0 ${transition}`}>·</span>
                <span className={`text-xs text-muted-foreground shrink-0 ${transition}`}>
                  {item.movieYear}
                </span>
              </>
            )}
            {item.movieRuntime && (
              <>
                <span className={`text-xs text-muted-foreground shrink-0 ${transition}`}>·</span>
                <span className={`inline-flex items-center gap-0.5 text-xs text-muted-foreground shrink-0 ${transition}`}>
                  <Clock className="size-3" />
                  {item.movieRuntime}
                </span>
              </>
            )}
            {item.movieRating && (
              <>
                <span className={`text-xs text-muted-foreground shrink-0 ${transition}`}>·</span>
                <span className={`inline-flex items-center gap-0.5 text-xs text-muted-foreground shrink-0 ${transition}`}>
                  <Star className="size-3 fill-current text-yellow-500" />
                  {item.movieRating}
                </span>
              </>
            )}
          </div>

          {item.movieGenres && (
            <div className="flex items-center gap-[11px]">
              {item.movieGenres.split(", ").map((genre) => (
                <span key={genre} className={`text-xs text-muted-foreground ${transition}`}>
                  #{genre}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div
          className={`flex items-center gap-2.5 shrink-0 ${isHovered ? "opacity-100" : "opacity-0"} ${transition}`}
        >
          <TooltipProvider>
            <div className="flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon-sm" className="bg-muted active:scale-95" tabIndex={-1} onClick={() => runAction(onQueue)}>
                    <Plus className="size-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <span>Queue</span>
                  <kbd className="ml-1.5 text-[10px] opacity-60">Q</kbd>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon-sm" className="bg-muted active:scale-95" tabIndex={-1} onClick={() => runAction(onDelete)}>
                    <X className="size-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <span>Delete</span>
                  <kbd className="ml-1.5 text-[10px] opacity-60">X</kbd>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="w-px h-8 bg-muted" />
            <div className="flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon-sm" className="bg-muted active:scale-95" tabIndex={-1} onClick={() => runAction(onComplete)}>
                    <Check className="size-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <span>Complete</span>
                  <kbd className="ml-1.5 text-[10px] opacity-60">C</kbd>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon-sm" className="bg-muted active:scale-95" tabIndex={-1} onClick={() => runAction(onBacklog)}>
                    <Archive className="size-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <span>Backlog</span>
                  <kbd className="ml-1.5 text-[10px] opacity-60">B</kbd>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon-sm" className="bg-muted active:scale-95" tabIndex={-1}>
                    <MoreVertical className="size-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>More</TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </div>
      </div>
    </div>
  );
});
